/**
 * DocViewSchema Registry
 * 
 * Central registry for all DocViewSchemas.
 * This is the single entry point for getting a doctype's view schema.
 * 
 * Rules of Engagement:
 * - All detail pages must obtain their schema via getDocViewSchema(doctype)
 * - No component should hardcode layout for a doctype - use the schema
 * - Missing schemas throw in dev (loud failure), fallback in prod (safe)
 * 
 * To add a new doctype:
 * 1. Create docViewSchemas/{doctype}.ts (copy from item.ts)
 * 2. Import and add to `schemas` map below
 * 3. Ensure widgets exist in EXPERIENCE_WIDGETS registry
 */

import type { DocViewSchema, DocViewVariantConfig } from "./types";
import { itemDocViewSchema } from "./item";
import { itemPriceDocViewSchema } from "./itemPrice";
import { pricingRuleDocViewSchema } from "./pricingRule";
import { stockEntryDocViewSchema } from "./stockEntry";
import { stockReconciliationDocViewSchema } from "./stockReconciliation";
import { salesInvoiceDocViewSchema } from "./salesInvoice";
import { storefrontThemeDocViewSchema } from "./storefrontTheme";
import { storefrontPageConfigDocViewSchema } from "./storefrontPageConfig";
import { tiktokListingDocViewSchema } from "./tiktokListing";
import { lazadaListingDocViewSchema } from "./lazadaListing";
import { shopeeListingDocViewSchema } from "./shopeeListing";
import { itemGroupDocViewSchema } from "./itemGroup";
import { itemCustomizationTemplateDocViewSchema } from "./itemCustomizationTemplate";
// NOTE: Sales Order is NOT registered here because it uses the legacy FormViewConfig approach.
// The Sales Order has a fully working experience view with SALES_ORDER_WIDGETS that relies on
// the detailed field layouts from salesOrderShopifyEcomView.formView.
// To migrate Sales Order to DocViewSchema, you would need to either:
// 1. Add widgetId to every section (and register all widgets in EXPERIENCE_WIDGETS)
// 2. OR copy all the field configurations from FormViewConfig into the schema
// For now, we keep using the legacy path which works correctly.
// import { salesOrderDocViewSchema } from "./salesOrder";

// Re-export types for external use
export type {
  DocViewSchema,
  DocViewSectionConfig,
  DocViewVariantConfig,
  DocViewTabConfig,
  DocViewLayout,
  DocViewMode,
  DocViewWidgetProps,
} from "./types";

/**
 * Schema registry: DocType name → DocViewSchema
 * 
 * Add new doctypes here as they are created.
 * Keys must match the exact Frappe DocType name.
 */
const schemas: Record<string, DocViewSchema> = {
  Item: itemDocViewSchema,
  "Item Price": itemPriceDocViewSchema,
  "Pricing Rule": pricingRuleDocViewSchema,
  "Stock Entry": stockEntryDocViewSchema,
  "Stock Reconciliation": stockReconciliationDocViewSchema,
  "Sales Invoice": salesInvoiceDocViewSchema,
  "Storefront Theme": storefrontThemeDocViewSchema,
  "Storefront Page Config": storefrontPageConfigDocViewSchema,
  "TikTok Listing": tiktokListingDocViewSchema,
  "Lazada Listing": lazadaListingDocViewSchema,
  "Shopee Listing": shopeeListingDocViewSchema,
  "Item Group": itemGroupDocViewSchema,
  "Item Customization Template": itemCustomizationTemplateDocViewSchema,
  // Sales Order uses legacy FormViewConfig - see note above
  // "Sales Order": salesOrderDocViewSchema,
};

/**
 * Create a safe fallback schema for production use.
 * This renders a generic "all fields" layout when no schema is defined.
 */
function createFallbackSchema(doctype: string): DocViewSchema {
  const fallbackVariant: DocViewVariantConfig = {
    sections: [
      {
        id: "fallback_all_fields",
        label: doctype,
        layout: "one-column",
        // Empty fields = renderer should use meta to show all fields
      },
    ],
  };

  return {
    doctype,
    views: {
      experience: fallbackVariant,
      system: fallbackVariant,
    },
  };
}

/**
 * Get DocViewSchema for a doctype.
 * 
 * This is the canonical entry point for obtaining a doctype's layout schema.
 * 
 * @param doctype - DocType name (e.g., "Item", "Sales Order")
 * @returns DocViewSchema for the doctype
 * 
 * @throws Error in development if schema is not registered
 * @example
 * const schema = getDocViewSchema("Item");
 * const experienceConfig = schema.views.experience;
 */
export function getDocViewSchema(doctype: string): DocViewSchema {
  const schema = schemas[doctype];

  if (!schema) {
    const message =
      `[DocViewSchema] No schema registered for doctype "${doctype}". ` +
      `Add one in src/lib/views/docViewSchemas/${doctype
        .toLowerCase()
        .replace(/\s+/g, "")}.ts and export it via index.ts.`;

    // Loud failure in development - forces devs/AI to add missing schema
    if (import.meta.env.DEV) {
      throw new Error(message);
    }

    // Safe fallback in production - log error but don't crash
    console.error(message);
    return createFallbackSchema(doctype);
  }

  return schema;
}

/**
 * Check if a doctype has a registered schema.
 * Useful for conditional rendering logic.
 */
export function hasDocViewSchema(doctype: string): boolean {
  return doctype in schemas;
}

/**
 * Get list of all registered doctypes.
 * Useful for debugging and admin tools.
 */
export function getRegisteredDoctypes(): string[] {
  return Object.keys(schemas);
}

